import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import styles from "./concurrencyConflictModal.module.scss";
import Button from "./Button";

/**
 * Three-column row-level conflict resolution UI shared across all
 * version-controlled detail pages. Designed so the user CANNOT lose
 * unsaved work: cancelling closes the modal without changing form state,
 * and resolving emits a structured choice the caller can apply onto
 * its own form state before resubmitting.
 */

export interface ConcurrencyConflict {
    entity: string;
    id: number;
    expectedVersion: number;
    actualVersion: number;
    lastChangedBy?: { userId: number; fullName?: string } | null;
    lastChangedAt?: string | null;
    canonical?: any;
}

export type ConflictResolution =
    | { kind: "keep-mine" }
    | { kind: "accept-theirs" }
    | { kind: "accept-both" };

export interface ResolvedConflicts {
    decisions: Map<string, ConflictResolution>;
    canonicalById: Map<string, any>;
    overwriteReason?: string;
}

interface Props {
    show: boolean;
    conflicts: ConcurrencyConflict[];
    /** The user's current form draft, keyed by entity name → row id → row.
     *  Used to render the "your draft" column. */
    userDraftLookup: (entityName: string, id: number) => any | undefined;
    /** Optional snapshot of the entity at load time for the "original" column.
     *  When undefined, the modal hides the original column gracefully. */
    originalLookup?: (entityName: string, id: number) => any | undefined;
    /** When true, the resolve button asks the user to type a reason
     *  before enabling submit (recommended for finance entities). */
    requireOverwriteReason?: boolean;
    onResolve: (resolved: ResolvedConflicts) => void;
    onCancel: () => void;
}

const conflictKey = (c: ConcurrencyConflict) => `${c.entity}:${c.id}`;

/**
 * Render a single value as something inspectable. Handles primitives,
 * dates, FK objects, and arrays without crashing on circular refs.
 */
function renderValue(value: any): string {
    if (value === null || value === undefined) return "—";
    if (value instanceof Date) return value.toISOString();
    if (typeof value === "object") {
        // FK objects (e.g. religion: { religionId, name }) — show a meaningful label
        if ("name" in value && typeof value.name === "string") return value.name;
        if ("fullName" in value && typeof value.fullName === "string") return value.fullName;
        try {
            return JSON.stringify(value);
        } catch {
            return "[object]";
        }
    }
    return String(value);
}

/**
 * Fields that should never appear in the conflict diff:
 *  - bookkeeping (version, audit timestamps, createdBy/updatedBy/deletedBy)
 *  - presentation-only S3 pre-signed URLs (photoFile, signedUrl) — the URL
 *    changes on every BE fetch even when the underlying file hasn't moved
 *  - computed/cached aggregates the FE recomputes anyway
 *  - upload-bookkeeping keys
 *  - cross-entity reference-number lookups that the FE resolves separately
 *    for header display (parentReferenceNumber, parentTaskReferenceNumber,
 *    originLeadReference) — the BE returns null for them and the FE fills
 *    them in from a side query
 */
const SKIP_KEYS = new Set([
    "version",
    "createdAt", "updatedAt", "deletedAt",
    "createdBy", "updatedBy", "deletedBy",
    "photoFile", "photoFileKey", "signedUrl",
    "fullName",
    "averageRating", "totalRatings",
    "clientMutationId", "forceOverwrite", "overwriteReason",
    "parentReferenceNumber", "parentTaskReferenceNumber",
    "originLeadReference",
]);

/**
 * Normalize values for comparison so trivial encoding differences don't
 * register as edits:
 *  - empty string ≈ null ≈ undefined
 *  - numeric strings compared as numbers
 *  - FK-like objects (organization, religion, jobPosition, country, …)
 *    compared by their primary key only — extra nested fields the BE
 *    loads but the FE doesn't round-trip don't show as conflicts
 *  - other objects compared by structural JSON
 */
function normalizeForCompare(value: any): string {
    if (value === null || value === undefined) return "__nil__";
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") return "__nil__";
        if (/^-?\d+(\.\d+)?$/.test(trimmed)) return String(parseFloat(trimmed));
        return trimmed;
    }
    if (typeof value === "number") return String(value);
    if (typeof value === "boolean") return value ? "true" : "false";
    if (value instanceof Date) return value.toISOString();
    if (typeof value === "object" && !Array.isArray(value)) {
        // FK-like: anything with a primary key field. Two sides referring
        // to the same FK row should compare equal regardless of how deeply
        // each side hydrated the joined entity.
        const idKey = Object.keys(value).find(k => k.endsWith("Id") || k === "id");
        if (idKey) {
            const id = value[idKey];
            if (typeof id === "number" || typeof id === "string") {
                return `__fk__:${idKey}:${id}`;
            }
        }
    }
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

function diffFields(a: any, b: any): string[] {
    const keySet = new Set<string>([
        ...(a ? Object.keys(a) : []),
        ...(b ? Object.keys(b) : []),
    ]);
    const keys = Array.from(keySet);
    const out: string[] = [];
    for (const key of keys) {
        if (SKIP_KEYS.has(key)) continue;
        if (key.endsWith("Id")) continue;
        if (key.endsWith("Key")) continue;
        // Skip relation arrays — children get their own conflict cards;
        // showing their stringified form at parent level is noise.
        const av = a?.[key];
        const bv = b?.[key];
        if (Array.isArray(av) || Array.isArray(bv)) continue;
        const an = normalizeForCompare(av);
        const bn = normalizeForCompare(bv);
        if (an !== bn) out.push(key);
    }
    return out.sort();
}

/**
 * Detect rows in relation arrays present in canonical but missing from draft —
 * almost always rows another user added since the current user loaded the page.
 * Returns one bucket per relation collection so the modal can display them.
 */
interface AddedByOther {
    collection: string;
    idField: string;
    label: (row: any) => string;
    rows: any[];
}

const RELATION_DESCRIPTORS: Array<{ collection: string; idField: string; label: (row: any) => string }> = [
    { collection: "userEmails", idField: "userEmailId", label: r => r.email ?? "(email)" },
    { collection: "userPhoneNumbers", idField: "userPhoneNumberId", label: r => r.phoneNumber ?? "(phone)" },
    { collection: "userAddresses", idField: "userAddressId", label: r => r.address ?? r.city?.name ?? r.country?.name ?? "(address)" },
    { collection: "userDocuments", idField: "documentId", label: r => r.name ?? "(document)" },
    { collection: "userLanguages", idField: "userLanguageId", label: r => r.language?.name ?? "(language)" },
    { collection: "userNationalities", idField: "userNationalityId", label: r => r.country?.name ?? r.country?.nationality ?? "(nationality)" },
    { collection: "userTags", idField: "userTagId", label: r => r.tag?.name ?? "(tag)" },
    { collection: "userPrices", idField: "priceId", label: r => r.rateType ? `${r.rateType} (${r.cost ?? 0}/${r.price ?? 0})` : "(price)" },
    { collection: "userOrganizationHistory", idField: "historyId", label: r => r.organization?.name ?? "(employment history)" },
];

function detectAddedByOther(canonical: any, draft: any): AddedByOther[] {
    if (!canonical) return [];
    const out: AddedByOther[] = [];
    for (const desc of RELATION_DESCRIPTORS) {
        const canonicalRows = (canonical[desc.collection] as any[]) ?? [];
        const draftRows = (draft?.[desc.collection] as any[]) ?? [];
        const draftIds = new Set(draftRows.map(r => r?.[desc.idField]).filter((v: any) => v !== null && v !== undefined));
        const added = canonicalRows.filter(r => {
            const id = r?.[desc.idField];
            if (!id) return false;
            if (r.deletedAt) return false;
            return !draftIds.has(id);
        });
        if (added.length > 0) {
            out.push({ collection: desc.collection, idField: desc.idField, label: desc.label, rows: added });
        }
    }
    return out;
}

export const ConcurrencyConflictModal: React.FC<Props> = ({
    show,
    conflicts,
    userDraftLookup,
    originalLookup,
    requireOverwriteReason,
    onResolve,
    onCancel,
}) => {
    const [decisions, setDecisions] = useState<Map<string, ConflictResolution>>(new Map());
    const [reason, setReason] = useState("");

    useEffect(() => {
        if (show) {
            setDecisions(new Map());
            setReason("");
        }
    }, [show, conflicts]);

    const allDecided = useMemo(
        () => conflicts.every(c => decisions.has(conflictKey(c))),
        [conflicts, decisions],
    );

    const anyKeepMine = useMemo(
        () => Array.from(decisions.values()).some(d => d.kind === "keep-mine"),
        [decisions],
    );

    const reasonRequired = !!requireOverwriteReason && anyKeepMine;
    const canResolve = allDecided && (!reasonRequired || reason.trim().length > 0);

    const decide = (key: string, kind: ConflictResolution["kind"]) => {
        setDecisions(prev => {
            const next = new Map(prev);
            next.set(key, { kind });
            return next;
        });
    };

    const handleResolve = () => {
        const canonicalById = new Map<string, any>();
        for (const c of conflicts) {
            if (c.canonical) canonicalById.set(conflictKey(c), c.canonical);
        }
        onResolve({
            decisions,
            canonicalById,
            overwriteReason: reason.trim() || undefined,
        });
    };

    return (
        <Modal show={show} onHide={onCancel} size="xl" backdrop="static" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Resolve conflicts</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className={styles.warningBanner}>
                    <strong>Someone else changed this record while you were editing.</strong> Pick what to do for each conflicting row. Your draft is preserved — closing this dialog leaves your unsaved work intact.
                </div>

                {conflicts.map(c => {
                    const key = conflictKey(c);
                    const decision = decisions.get(key);
                    const draft = userDraftLookup(c.entity, c.id);
                    const original = originalLookup?.(c.entity, c.id);
                    const canonical = c.canonical;

                    const changedKeys = diffFields(canonical, draft);
                    const addedByOther = detectAddedByOther(canonical, draft);

                    return (
                        <div key={key} className={styles.conflictCard}>
                            <div className={styles.cardHeader}>
                                <div>
                                    <span className={styles.entityBadge}>{c.entity}</span>
                                    <span className={styles.metaText}>id {c.id}</span>
                                    {c.lastChangedBy?.fullName && (
                                        <span className={styles.metaText} style={{ marginLeft: 8 }}>
                                            • last changed by <strong>{c.lastChangedBy.fullName}</strong>
                                            {c.lastChangedAt && (
                                                <> at {new Date(c.lastChangedAt).toLocaleString()}</>
                                            )}
                                        </span>
                                    )}
                                </div>
                                <div className={styles.versionInfo}>
                                    your version: {c.expectedVersion} • current: {c.actualVersion}
                                </div>
                            </div>

                            {addedByOther.length > 0 && (
                                <div className={styles.addedAlert}>
                                    <strong>Other user also added:</strong>
                                    <ul>
                                        {addedByOther.map(group => (
                                            <li key={group.collection}>
                                                {group.collection}: {group.rows.map(r => group.label(r)).join(", ")}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className={styles.hint}>
                                        These will be merged into your contact when you pick "Accept both" or "Accept theirs only."
                                    </div>
                                </div>
                            )}

                            {/* Compute per-field provenance so the user can see at a glance
                                which fields each side touched and whether the changes overlap.
                                When changes are disjoint, "Accept both" is the cleanest action. */}
                            {(() => {
                                const fieldStatus = changedKeys.map(field => {
                                    const userChanged = original !== undefined
                                        ? JSON.stringify(draft?.[field]) !== JSON.stringify(original?.[field])
                                        : true;
                                    const serverChanged = original !== undefined
                                        ? JSON.stringify(canonical?.[field]) !== JSON.stringify(original?.[field])
                                        : true;
                                    return { field, userChanged, serverChanged };
                                });
                                const hasOverlap = fieldStatus.some(s => s.userChanged && s.serverChanged);
                                const isDisjoint = original !== undefined && !hasOverlap && changedKeys.length > 0;

                                return (
                                    <>
                                        {changedKeys.length === 0 && addedByOther.length === 0 ? (
                                            <div className={styles.noDiff}>No field-level differences detected (whole row may have been replaced).</div>
                                        ) : changedKeys.length === 0 ? null : (
                                            <div className="table-responsive">
                                                <table className={styles.diffTable}>
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: "20%" }}>Field</th>
                                                            {original !== undefined && <th style={{ width: "22%" }}>Original (when you loaded)</th>}
                                                            <th style={{ width: original !== undefined ? "23%" : "35%" }}>Current on server</th>
                                                            <th style={{ width: original !== undefined ? "23%" : "35%" }}>Your draft</th>
                                                            <th style={{ width: "12%" }}>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {fieldStatus.map(({ field, userChanged, serverChanged }) => (
                                                            <tr key={field}>
                                                                <td><span className={styles.fieldName}>{field}</span></td>
                                                                {original !== undefined && <td className={styles.untouched}>{renderValue(original?.[field])}</td>}
                                                                <td className={serverChanged ? "" : styles.untouched}>{renderValue(canonical?.[field])}</td>
                                                                <td className={`${userChanged ? styles.userValue : styles.untouched}`}>{renderValue(draft?.[field])}</td>
                                                                <td>
                                                                    {userChanged && serverChanged && <span className={`${styles.statusBadge} ${styles.overlap}`}>overlap</span>}
                                                                    {userChanged && !serverChanged && <span className={`${styles.statusBadge} ${styles.yours}`}>yours</span>}
                                                                    {!userChanged && serverChanged && <span className={`${styles.statusBadge} ${styles.theirs}`}>theirs</span>}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        <div className={styles.actionRow}>
                                            <Button
                                                type="button"
                                                variant={decision?.kind === "accept-both" ? "primary" : "secondary"}
                                                onClick={() => decide(key, "accept-both")}
                                                disabled={original === undefined}
                                                title={
                                                    original === undefined
                                                        ? "Original snapshot not available — pick mine or theirs"
                                                        : isDisjoint
                                                            ? "Recommended: changes don't overlap"
                                                            : "Yours wins for overlapping fields, theirs wins for the rest"
                                                }
                                            >
                                                {isDisjoint && <span className={styles.starHint}>★</span>}
                                                Accept both
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={decision?.kind === "keep-mine" ? "primary" : "secondary"}
                                                onClick={() => decide(key, "keep-mine")}
                                            >
                                                Keep mine only
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={decision?.kind === "accept-theirs" ? "primary" : "secondary"}
                                                onClick={() => decide(key, "accept-theirs")}
                                            >
                                                Accept theirs only
                                            </Button>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    );
                })}

                {reasonRequired && (
                    <div className={styles.reasonBox}>
                        <label>Reason for overwrite (required)</label>
                        <textarea
                            rows={2}
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="Why are you overwriting the other user's changes?"
                        />
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" onClick={onCancel}>
                    Cancel — keep my draft
                </Button>
                <Button type="button" variant="primary" disabled={!canResolve} onClick={handleResolve}>
                    Resolve and save
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ConcurrencyConflictModal;
